import { useMounted } from '@vueuse/core'
import { useQuasar } from 'quasar'
import { computed, reactive } from 'vue'

import { ssrScreenName } from '@/utils/ssrScreenName'

type ScreenName = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const SIZE_INDEX: Record<ScreenName, number> = {
  lg: 3,
  md: 2,
  sm: 1,
  xl: 4,
  xs: 0,
}

/**
 * SSR on desktop; wait for client mount on mobile (`lt.md`).
 * Used so overlay widgets (badges) stay out of the mobile LCP HTML.
 */
export const shouldSsrOnDesktop = ({
  isLtMd,
  isMounted,
}: {
  isLtMd: boolean
  isMounted: boolean
}): boolean => !isLtMd || isMounted

/**
 * Reactive screen info that is stable across the SSR → hydration boundary.
 *
 * - SSR + client until `onMounted`: UA-string bucket from `ssrScreenName`
 *   (identical on both sides for the same User-Agent).
 * - After mount: real `$q.screen.name` from the viewport.
 */
export function useSsrSafeScreen() {
  const $q = useQuasar()
  const isMounted = useMounted()

  const name = computed<ScreenName>(() => {
    // Only trust the measured viewport after mount. During hydration,
    // `$q.platform` / `$q.screen` can already diverge from the server
    // (Quasar iPad Mac-UA correction, Screen defaults, etc.).
    if (process.env.CLIENT && isMounted.value) {
      return $q.screen.name as ScreenName
    }

    return ssrScreenName($q)
  })

  const idx = computed(() => SIZE_INDEX[name.value])

  return reactive({
    gt: {
      lg: computed(() => idx.value > 3),
      md: computed(() => idx.value > 2),
      sm: computed(() => idx.value > 1),
      xs: computed(() => idx.value > 0),
    },
    lg: computed(() => idx.value === 3),
    lt: {
      lg: computed(() => idx.value < 3),
      md: computed(() => idx.value < 2),
      sm: computed(() => idx.value < 1),
      xl: computed(() => idx.value < 4),
    },
    md: computed(() => idx.value === 2),
    name,
    sm: computed(() => idx.value === 1),
    xl: computed(() => idx.value === 4),
    xs: computed(() => idx.value === 0),
  })
}
